import React, { useState, useEffect } from 'react';
import { useDraftOrders } from '../hooks/useDraftOrders';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { orderService } from '../../../services/firebaseService';
import { useAuth } from '../../../contexts/AuthContext';
import { transformPaymentPeriodLabel } from '../../../utils/orderUtils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isNaN(d) ? dateStr : d.toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' });
}

function statusBadge(status) {
  const map = {
    draft:              'bg-yellow-100 text-yellow-800',
    converted:          'bg-emerald-100 text-emerald-800',
    confirmed:          'bg-emerald-100 text-emerald-800',
    accepted:           'bg-green-100 text-green-800',
    cancelled:          'bg-red-100 text-red-800',
    revision_requested: 'bg-orange-100 text-orange-800',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-700';
  const label = status === 'revision_requested' ? 'revision requested' : (status || 'unknown');
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${cls}`}>
      {label}
    </span>
  );
}

// ─── Section heading helper ────────────────────────────────────────────────────

function SectionHeading({ icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-lg">{icon}</span>
      <h3 className="text-sm font-bold text-[#022b73] uppercase tracking-wide">{title}</h3>
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between py-1.5 border-b border-gray-100 last:border-0 text-sm">
      <span className="text-gray-500 shrink-0 mr-4">{label}</span>
      <span className="font-medium text-gray-800 text-right">{value}</span>
    </div>
  );
}

function CustomerDecisionSection({ decision }) {
  if (!decision) return null;

  const getRequirementLabel = (key) => {
    const labels = {
      productChoice: 'Product Choice',
      paymentPeriod: 'Payment Period',
      others: 'Other Terms'
    };
    return labels[key] || key;
  };

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 md:col-span-2">
      <SectionHeading icon="💬" title="Customer Feedback" />
      <div className="space-y-4">
        <div>
          <p className="text-xs text-orange-600 font-bold uppercase mb-2">Requested Changes</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(decision.requirements || {}).map(([key, value]) => value && (
              <span key={key} className="bg-white border border-orange-200 text-orange-800 px-3 py-1 rounded-lg text-xs font-medium shadow-sm">
                ✅ {getRequirementLabel(key)}
              </span>
            ))}
          </div>
        </div>
        
        {decision.feedback && (
          <div>
            <p className="text-xs text-orange-600 font-bold uppercase mb-1">Feedback Notes</p>
            <div className="bg-white border border-orange-200 rounded-lg p-3 text-sm text-gray-800 shadow-sm italic">
              "{decision.feedback}"
            </div>
          </div>
        )}

        <div className="flex justify-between items-center text-xs text-orange-500 pt-2 border-t border-orange-100">
          <span>Action: {decision.action?.replace('_', ' ')}</span>
          <span>Submitted: {fmt(decision.submitted_at)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Panel — full screen ────────────────────────────────────────────────

function DraftOrderDetail({ order, onClose, onConverted }) {
  const { currentAgent } = useAuth();
  const pp = order.paymentPlan || {};
  const pmt = order.payment || {};
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // Payment form state
  const [payMethod, setPayMethod] = useState(pmt.method || '');
  const [payRef, setPayRef] = useState(pmt.reference || '');
  const [payAmount, setPayAmount] = useState(pmt.amount_paid_local_currency || '');
  const [showAllCustomer, setShowAllCustomer] = useState(false);
  const [showAllAgent, setShowAllAgent] = useState(false);

  const isPaymentValid = payMethod && payRef && payAmount && !isNaN(Number(payAmount));

  const handleConvert = async () => {
    if (!isPaymentValid) {
      setError('Please provide valid payment details before converting.');
      return;
    }

    if (!window.confirm('Are you sure you want to convert this draft into an official system order? This action cannot be undone.')) {
      return;
    }

    setIsConverting(true);
    setError(null);
    try {
      // Pass updated payment details to the confirmation service
      const updatedPayment = {
        ...pmt,
        method: payMethod,
        reference: payRef,
        amount_paid_local_currency: payAmount,
        status: payMethod.toLowerCase() === 'cash' ? 'paid' : 'pending'
      };

      const result = await orderService.confirmDraftOrder(order.id, currentAgent.id, updatedPayment, currentAgent.role);
      if (result.success) {
        setSuccessData(result);
        if (onConverted) onConverted();
        
        // Auto-close after success to return to list
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error('Failed to convert draft order.');
      }
    } catch (err) {
      console.error('Conversion error:', err);
      setError(err.message || 'An error occurred during conversion.');
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-100 overflow-y-auto">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-[#022b73] text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div>
          <p className="text-xs text-blue-200 font-medium tracking-widest uppercase">Draft Order</p>
          <h2 className="text-xl font-bold leading-tight">{order.draftOrderNumber || order.code || order.id}</h2>
          <div className="mt-1 flex items-center gap-3 text-xs text-blue-200">
            <span>Created {fmt(order.createdAt || order.created_at)}</span>
            <span>·</span>
            <span>Expires {fmt(order.expiresAt || order.expires_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {statusBadge(order.status)}
          <button
            onClick={onClose}
            className="ml-4 text-white/70 hover:text-white text-3xl leading-none font-light"
            aria-label="Close"
          >
            ×
          </button>
        </div>
      </div>

      {/* Body — two-column on wide screens */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        
        {/* Success / Error Alerts */}
        {successData && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-6 text-center shadow-sm">
            <div className="text-green-600 text-4xl mb-2">✅</div>
            <h3 className="text-xl font-bold text-green-800">Successfully Converted!</h3>
            <p className="text-green-700 mt-1">
              Draft has been promoted to Official Order: <span className="font-mono font-bold text-green-900">{successData.orderNumber}</span>
            </p>
            <div className="mt-4 flex justify-center gap-3">
               <button
                onClick={onClose}
                className="px-6 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3 shadow-sm">
            <span className="text-xl">⚠️</span>
            <p className="font-medium">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* ── FINALIZATION ACTION BAR ── */}
          {!successData && ['draft', 'sent', 'accepted', 'revision_requested'].includes(order.status) && (
            <div className="bg-white border-2 border-[#022b73]/20 rounded-xl shadow-md p-6 md:col-span-2">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <SectionHeading icon="⚡" title="Finalize & Convert" />
                  <p className="text-sm text-gray-500 mb-4">
                    Verify payment details below. Once confirmed, this draft becomes a permanent system order.
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Payment Method</label>
                      <select 
                        value={payMethod} 
                        onChange={(e) => setPayMethod(e.target.value)}
                        className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#022b73] outline-none"
                      >
                        <option value="">Select Method</option>
                        <option value="Cash">Cash</option>
                        <option value="Payment Link">Payment Link</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Reference / Receipt</label>
                      <input 
                        type="text" 
                        value={payRef} 
                        onChange={(e) => setPayRef(e.target.value)}
                        placeholder="e.g. M-Pesa ID"
                        className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#022b73] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Amount Paid ({order.currency})</label>
                      <input 
                        type="text" 
                        value={payAmount} 
                        onChange={(e) => setPayAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#022b73] outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center shrink-0">
                  <button
                    onClick={handleConvert}
                    disabled={isConverting || !isPaymentValid}
                    className={`w-full md:w-auto px-8 py-4 rounded-xl font-bold text-white shadow-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                      isConverting || !isPaymentValid 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-[#022b73] hover:bg-[#033a9c] hover:scale-[1.02] active:scale-[0.98]'
                    }`}
                  >
                    {isConverting ? <LoadingSpinner size="sm" color="white" /> : '🚀'}
                    {isConverting ? 'Processing...' : 'Convert to Order'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── CUSTOMER FEEDBACK PORTAL ── */}
          {order.customer_decision && (
             <CustomerDecisionSection decision={order.customer_decision} />
          )}

          {/* ── CUSTOMER ── */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <SectionHeading icon="👤" title="Customer Details" />
            <InfoRow label="Name" value={order.customer?.name} />
            <InfoRow label="Phone" value={order.customer?.mobile} />
            <InfoRow label="Email" value={order.customer?.email} />
            
            <div className="mt-2">
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Shipping Address</p>
              <p className="text-sm text-gray-800 bg-gray-50 p-2 rounded-lg border border-gray-100">
                {order.customer?.shipping_address || '—'}
              </p>
            </div>
          </div>

          {/* ── AGENT ── */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <SectionHeading icon="🏷️" title="Order Context" />
            <InfoRow label="Agent Name" value={order.agent?.name} />
            <InfoRow label="Agent ID" value={order.agent_id} />
            <InfoRow label="Currency" value={order.currency} />
            <InfoRow label="Referrer" value={order.referrer || '—'} />
          </div>

          {/* ── ITEMS ── */}
          {Array.isArray(order.items) && order.items.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5 md:col-span-2">
              <SectionHeading icon="📦" title="Order Items" />
              <div className="space-y-3">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center gap-4 py-2 border-b border-gray-50 last:border-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800 text-sm">{item.name}</p>
                        {item.is_gift === 'true' && (
                          <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-semibold">🎁 Gift</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">SKU: {item.sku} · Qty: {item.quantity}</p>
                    </div>
                    <div className="text-right shrink-0">
                      {item.is_gift !== 'true' && (
                        <p className="text-sm font-bold text-gray-800">
                          {order.currency} {item.subtotal_local_currency}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── PAYMENT ── */}
          {pmt && Object.keys(pmt).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <SectionHeading icon="💳" title="Payment" />
              <InfoRow label="Status" value={pmt.status} />
              <InfoRow label="Method" value={pmt.method} />
              <InfoRow label="Reference" value={pmt.reference} />
              <InfoRow label={`Total ${pmt.local_currency || order.currency}`} value={pmt.total_amount_local_currency} />
              <InfoRow label={`Paid ${pmt.local_currency || order.currency}`} value={pmt.amount_paid_local_currency} />
            </div>
          )}

          {/* ── PAYMENT PLAN ── */}
          {pp && Object.keys(pp).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <SectionHeading icon="📅" title="Payment Plan" />
              <InfoRow label="Period" value={transformPaymentPeriodLabel(order.paymentPeriod)} />
              <InfoRow label="Instalment Amount" value={pp.ins_amount ? `${order.currency} ${pp.ins_amount}` : null} />
              <InfoRow label="Frequency" value={pp.ins_frequency} />
              <InfoRow label="Max Instalments" value={pp.max_no_of_ins} />
              <InfoRow label="Final Instalment Date" value={pp.final_ins_date || null} />
            </div>
          )}

          {/* ── HISTORY ── */}
          {Array.isArray(order.history) && order.history.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5 md:col-span-2">
              <SectionHeading icon="🕒" title="History" />
              <ol className="relative border-l border-gray-200 ml-2 space-y-4">
                {order.history.map((entry, i) => (
                  <li key={i} className="ml-4">
                    <div className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-[#022b73] border-2 border-white" />
                    <div className="bg-gray-50 rounded-lg p-3 text-sm">
                      <div className="flex justify-between flex-wrap gap-1 mb-1">
                        <span className="font-semibold text-gray-800 capitalize">
                          {entry.action?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-400">{fmt(entry.timestamp)}</span>
                      </div>
                      {entry.notes && <p className="text-gray-600">{entry.notes}</p>}
                      {entry.actor && (
                        <p className="text-xs text-gray-400 mt-1">
                          By: {entry.actor}{entry.actorId ? ` (${entry.actorId})` : ''}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* ── CUSTOMER LINK ── */}
          {order.customerLink && (
            <div className="bg-white rounded-xl shadow-sm p-5 md:col-span-2">
              <SectionHeading icon="🔗" title="Customer Link" />
              <a
                href={order.customerLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm break-all"
              >
                {order.customerLink}
              </a>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Row Card ──────────────────────────────────────────────────────────────────

function DraftOrderCard({ order, onSelect }) {
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#022b73]/40 transition-all duration-200 cursor-pointer p-4"
      onClick={() => onSelect(order)}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-[#022b73] truncate">{order.draftOrderNumber || order.code || order.id}</p>
          <p className="text-sm text-gray-600 mt-0.5">{order.customer?.name || '—'}</p>
          <p className="text-xs text-gray-400 mt-1">{fmt(order.createdAt || order.created_at)}</p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {statusBadge(order.status)}
          <p className="font-bold text-gray-800 text-sm">{order.currency} {order.subtotal_local}</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between text-xs text-gray-500">
        <span>Agent: {order.agent?.name || order.agent_id || '—'}</span>
        <span>{order.items_count || 0} item(s) · {order.currency}</span>
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function DraftOrdersPage() {
  const { orders, loading, error, reload } = useDraftOrders();
  const [selected, setSelected] = useState(null);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-red-600 font-medium mb-4">⚠️ {error}</p>
        <button
          onClick={reload}
          className="px-4 py-2 bg-[#022b73] text-white rounded-lg text-sm hover:bg-[#022b73]/90 transition"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="pb-10">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#022b73]">📋 Draft Orders</h1>
          <p className="text-gray-500 text-sm mt-1">
            {orders.length} draft order{orders.length !== 1 ? 's' : ''} found
          </p>
        </div>
        <button
          onClick={reload}
          className="px-4 py-2 border border-[#022b73]/30 text-[#022b73] rounded-lg text-sm hover:bg-[#022b73]/5 transition"
        >
          🔄 Refresh
        </button>
      </div>

      {/* List */}
      {orders.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-lg">No draft orders found.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {orders.map((order) => (
            <DraftOrderCard key={order.id} order={order} onSelect={setSelected} />
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <DraftOrderDetail 
          order={selected} 
          onClose={() => setSelected(null)} 
          onConverted={reload}
        />
      )}
    </div>
  );
}
